import BaseChatbotService from './base-chatbot';
import config from 'later/config/environment';
import { fetch } from 'later/utils/fetch';
import loadScript, { ScriptLoadError } from 'later/utils/load-script';
import { STRATEGY } from 'shared/utils/retry';

import type { UserAndAccountTraits } from './chatbot-wrapper';

interface ChatbotUserAttributesResponse {
  token: string;
}

const MAVEN_WIDGET_URL = 'https://chat.onmaven.app/js/widget.js';

export default class MavenService extends BaseChatbotService {
  readonly name = 'maven';
  #loadError?: ScriptLoadError;

  async setup(userAndAccountTraits: UserAndAccountTraits): Promise<void> {
    // Note: canChatbot is false for certain staging QA users to prevent
    // widget from interfering with locators on auto tests
    if (!this.auth.currentAccount?.canChatbot || !this.auth.currentAccount?.rolloutMavenChatbot) {
      return;
    }

    try {
      await loadScript(MAVEN_WIDGET_URL, {
        id: 'maven-snippet'
      });
      this.#loadError = undefined;

      if (window.Maven?.ChatWidget) {
        const securedData = await this.fetchUserAttributesToken();

        window.Maven.ChatWidget.load({
          organizationId: 'later',
          agentId: 'support',
          // Autoplay's live-activity MCP tools expect a constant `product_id` on
          // unsignedUserData (https://developers.autoplay.ai/recipes/maven/step-1-connect-real-time-events).
          // Merged in alongside the existing traits rather than replacing them, since Maven's
          // own persona logic already relies on the rest of userAndAccountTraits.
          unsignedUserData: { ...userAndAccountTraits, product_id: config.APP.autoplayProductId },
          signedUserData: securedData ?? '',
          bgColor: '#1E1E1E',
          showPoweredBy: false
        });

        window.addEventListener('mavenWidgetOpened', () => {
          this.onOpen();
        });

        window.addEventListener('mavenWidgetClosed', () => {
          this.integrationsQueue.removeFromQueue('chatbot');
        });
      }
    } catch (error) {
      if (error instanceof ScriptLoadError && error.url === MAVEN_WIDGET_URL) {
        // Automatic loading is optional. The support action below provides a fallback if requested.
        this.#loadError = error;
        return;
      }
      this.errors.log('Maven failed to load', error);
      this.displayError();
    }
  }

  show(): void {
    window.Maven?.ChatWidget?.show();
  }

  hide(): void {
    window.Maven?.ChatWidget?.hide();
  }

  open(): void {
    if (!window.Maven?.ChatWidget) {
      this.errors.log(this.#loadError ?? new Error('Maven widget is unavailable'), {
        operation: 'openMaven',
        scriptUrl: MAVEN_WIDGET_URL
      });
      this.displayError();
      return;
    }
    if (!window.Maven.ChatWidget.isOpen()) {
      window.Maven.ChatWidget.open();
    }
  }

  close(): void {
    window.Maven?.ChatWidget?.close();
  }

  reset(): void {
    // No reset functionality for Maven Chatbot
  }

  // Helper method to fetch secured user data token for Maven. Fail silently and log if an error occurs,
  // as the chatbot can still function with just the unsigned data.
  async fetchUserAttributesToken(): Promise<string | undefined> {
    try {
      const url = '/api/v2/users/chatbot_user_attributes';
      const response: ChatbotUserAttributesResponse = await fetch(
        url,
        {},
        { intl: null, numRetries: 1, retryStrategy: STRATEGY.DEFAULT, raw: false }
      );
      return response.token;
    } catch (error) {
      this.errors.log(`Maven failed to fetch user attributes for User ID: ${this.auth.currentUserModel?.id}`, error);
      return undefined;
    }
  }
}

declare module '@ember/service' {
  interface Registry {
    'chatbot/maven': MavenService;
  }
}
